import React, { useEffect, useRef, useState } from 'react';

// Hooks
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  fetchOrderContactDetails,
  orderBulkActions,
  fetchOrderOverviewDetails,
} from '~/redux/actions/orders-actions';
import { failure, success } from '~/redux/actions/snackbar-actions';
import { searchAndAdvanceChange } from '~/redux/actions/orders-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { debounce } from '~/utils/helperFunctions';

// Components
import FilterModal from './FilterModal';
import ContactListing from './Table';
import ConfirmModal from '~/components/General/ConfirmModal';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcButton from '~/components/General/OlcButton';

// MUI Components
import {
  Container,
  Grid,
  Box,
  Input,
  InputAdornment,
  IconButton,
  Typography,
  MenuItem,
} from '@mui/material';

// Icons
import {
  TemplateSearchOneCIcon,
  DeleteContactsCIcon,
  CancelOrderCIcon,
  Remove2CIcon,
  Export2CIcon,
} from '~/assets/images';
import HighlightOffIcon from '@mui/icons-material/HighlightOffOutlined';
import CloseIcon from '@mui/icons-material/Close';

// Styles
import './styles.scss';

/**
 * Renders a search input, a filter button, a create button, and a template listing component.
 * Handles navigation to the template builder page and manages the state of the search input.
 *
 * @returns {JSX.Element} The rendered Templates component.
 */
const OrderContacts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasMountedSearchEffect = useRef(false);
  const [isOpenAdvanceFilter, setIsOpenAdvanceFilter] = useState(false);
  const [searchApplied, setSearchApplied] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // Actions State
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsOpen = Boolean(actionsAnchorEl);
  const handleActionsClick = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };
  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };
  // Actions State

  // Modal State
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  const { id } = useParams();

  const search = useSelector((state) => state.ordersReducers.detailContacts.search);
  const contactsPagination = useSelector(
    (state) => state.ordersReducers.detailContacts.contactsPagination
  );

  const filters = useSelector((state) => state.ordersReducers.detailContacts.filters);

  const filterSize = Object.keys(filters).length;

  const selectedOrder = useSelector((state) => state.ordersReducers.detailOverview.stepper);
  const orderOverview = useSelector((state) => state.ordersReducers.detailOverview.overview);

  const totalContactsInDB = useSelector(
    (state) => state.ordersReducers.detailContacts.totalOrderContactsInDB
  );

  const getAllContacts = (payload) => {
    dispatch(
      fetchOrderContactDetails({
        id,
        pagination: contactsPagination,
        search,
        ...payload,
      })
    );
  };

  const handleSearch = () => {
    if (search) {
      setSearchApplied(true);
      filterSize > 0 ? getAllContacts({ filters, applyFilters: true }) : getAllContacts();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && search) {
      event.preventDefault();
      setSearchApplied(true);
      filterSize > 0 ? getAllContacts({ filters, applyFilters: true }) : getAllContacts();
    }
  };

  const debouncedHandleKeyDown = debounce(handleKeyDown, 300);
  const debouncedHandleSearch = debounce(handleSearch, 300);

  const clearAdvanceFilter = () => {
    dispatch(searchAndAdvanceChange('filters', {}));
    getAllContacts({ pagination: { ...contactsPagination, page: DEFAULT_PAGINATION.page } });
  };

  const handleCloseDialog = () => {
    setDialog({
      open: false,
      message: '',
      loading: false,
      params: null,
      currentAction: null,
    });
  };

  const handleOpenDelete = () => {
    setOpenConfirmModal({
      type: 'delete',
      open: true,
      loading: false,
      message:
        selectedRows[0] === -1 || selectedRows.length > 1
          ? MESSAGES.ORDERS.DETAIL.CONTACTS.REMOVE.MODAL.REMOVE_CONTACT.REMOVE_CONTACTS
          : MESSAGES.ORDERS.DETAIL.CONTACTS.REMOVE.MODAL.REMOVE_CONTACT.REMOVE_SINGLE_CONTACTS,
      subMessage: MESSAGES.ORDERS.DETAIL.CONTACTS.REMOVE.MODAL.REMOVE_CONTACT.NOTE,
      header:
        selectedRows[0] === -1 || selectedRows.length > 1
          ? MESSAGES.ORDERS.DETAIL.CONTACTS.REMOVE.MODAL.REMOVE_CONTACT.MULTI_REMOVE_HEADING
          : MESSAGES.ORDERS.DETAIL.CONTACTS.REMOVE.MODAL.REMOVE_CONTACT.SINGLE_REMOVE_HEADING,
      component: (
        <Box className="mainImg">
          <img src={CancelOrderCIcon} alt="remove contacts from order" />
        </Box>
      ),
      handleClose: () => {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      },
      successButtonName: 'Yes',
      handleOk: () => {
        deleteContacts();
      },
    });
  };

  const deleteContacts = async () => {
    try {
      setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'remove order items',
        orderId: id,
        search: search.length ? search : null,
        applyFilters: filterSize > 0 ? true : false,
        filters: filterSize > 0 ? filters : null,
      };
      const response = await dispatch(orderBulkActions(payload, true));
      if (response?.status === 200) {
        getAllContacts();
        setSelectedRows([]);
        dispatch(success(response.data?.message));
        const result = await dispatch(fetchOrderOverviewDetails({ id }));

        if (result && result.status === 422 && result.shouldRedirect) {
          navigate('/orders');
        }
      } else {
        dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      }, 1000);
    }
  };

  useEffect(() => {
    if (!hasMountedSearchEffect.current) {
      hasMountedSearchEffect.current = true;
      return;
    }

    if (!search) {
      setSearchApplied(false);
      filterSize > 0 ? getAllContacts({ filters, applyFilters: true }) : getAllContacts();
    }
  }, [search]);

  useEffect(() => {
    return () => {
      dispatch(searchAndAdvanceChange('filters', {}));
      dispatch(searchAndAdvanceChange('search', ''));
    };
  }, []);

  return (
    <Box className="order_contact_wrapper">
      <Container maxWidth={false} disableGutters className="orderContactsContainer">
        {isOpenAdvanceFilter && (
          <FilterModal
            open={isOpenAdvanceFilter}
            handleClose={() => setIsOpenAdvanceFilter(false)}
            search={search}
            handleSearch={() => {}}
          />
        )}
        <Grid container rowSpacing={2}>
          <Grid
            item
            lg={9}
            md={12}
            sm={12}
            xs={12}
            className="searchBtnWrapper newSearchBtnWrapper"
          >
            <Box className="searchWrapper">
              {/* Search Input */}
              <Input
                className={`searchInput ${searchApplied ? 'searchApplied' : ''}`}
                type="text"
                autoComplete="off"
                value={search}
                name="search"
                onKeyDown={debouncedHandleKeyDown}
                onChange={(e) =>
                  dispatch(searchAndAdvanceChange(e.target.name, e.target.value.trimStart()))
                }
                sx={{
                  '@media (width < 1200px)': {
                    maxWidth: '100% !important',
                  },
                }}
                placeholder="Search by name, company, address, phone, or email"
                endAdornment={
                  <InputAdornment position="end" className="searchInputAdornment">
                    <IconButton
                      aria-label="toggle password visibility"
                      edge="end"
                      className="searchIcon"
                      onClick={debouncedHandleSearch}
                    >
                      <img src={TemplateSearchOneCIcon} alt="search" />
                    </IconButton>
                    {/* {searchApplied && (
                      <HighlightOffIcon
                        onClick={() => {
                          dispatch(searchAndAdvanceChange('search', ''));
                        }}
                        className="clearSerach"
                      />
                    )} */}
                    {search?.trim() && (
                      <IconButton
                        className="cpClearInputBtn"
                        size="small"
                        onClick={() => {
                          dispatch(searchAndAdvanceChange('search', ''));
                          setSearchApplied(false);
                        }}
                        edge="end"
                        sx={{ padding: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
              />

              {/* Filter Button */}
              <Box className="filterBtnWrapper">
                <OlcButton
                  variant="secondary"
                  onClick={() => {
                    setIsOpenAdvanceFilter(true);
                  }}
                >
                  Filters
                </OlcButton>
                {filterSize > 0 && <Typography className="filtersCounter">{filterSize}</Typography>}
              </Box>
              {filterSize > 0 && (
                <OlcButton
                  variant="secondary"
                  onClick={clearAdvanceFilter}
                  className="clearFilterBtn"
                  sx={{
                    '@media (width < 1200px)': {
                      width: '180px !important',
                    },
                  }}
                >
                  {MESSAGES.ORDERS.DETAIL.CONTACTS.CLEAR_FILTERS}
                </OlcButton>
              )}
            </Box>
          </Grid>
          <Grid item lg={3} md={12} sm={12} xs={12} className="removeBtnWrapper">
            {orderOverview?.isCancellable &&
              (selectedOrder?.status == 'On Hold' || selectedOrder?.status == 'Scheduled') &&
              selectedRows.length > 0 && (
                <>
                  <OlcButton
                    variant="secondary"
                    id="order-contact-actions-button"
                    aria-controls={actionsOpen ? 'order-contact-actions-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={actionsOpen ? 'true' : undefined}
                    onClick={handleActionsClick}
                    dropdown
                    expanded={actionsOpen}
                  >
                    {MESSAGES.CONTACTS.CONTACT_ACTIONS}
                  </OlcButton>
                  <OlcActionMenu
                    id="order-contact-actions-menu"
                    anchorEl={actionsAnchorEl}
                    open={actionsOpen}
                    onClose={handleActionsClose}
                    MenuListProps={{
                      'aria-labelledby': 'order-contact-actions-button',
                    }}
                    disableScrollLock
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    sx={{
                      marginTop: '4px',
                    }}
                  >
                    <MenuItem
                      className="menuItem"
                      onClick={() => {
                        handleOpenDelete();
                        handleActionsClose();
                      }}
                    >
                      <img src={Remove2CIcon} alt="icon" />
                      {MESSAGES.ORDERS.DETAIL.CONTACTS.REMOVE.CONTACT}
                    </MenuItem>
                    {/* <MenuItem
                    className="menuItem"
                    onClick={() => {
                      setTagsUpdateModal({
                        open: true,
                        type: 'update',
                      });
                      handleActionsClose();
                    }}
                  >
                    <img src={Export2CIcon} alt="icon" />
                    {MESSAGES.CONTACTS.ACTIONS.EXPORT_CONTACTS}
                  </MenuItem> */}
                  </OlcActionMenu>
                </>
              )}
          </Grid>
        </Grid>

        {/* Contact Listing Component */}
        <ContactListing
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
          searchApplied={searchApplied}
          totalContactsInDB={totalContactsInDB}
          filterSize={filterSize}
          filters={filters}
        />

        {/* Delete Modal */}
        {openConfirmModal.type === 'delete' && <ConfirmModal openConfirmModal={openConfirmModal} />}
      </Container>
    </Box>
  );
};

export default OrderContacts;
